export interface ImageValidationResult {
  isValid: boolean;
  format: string;
  dimensions: { width: number; height: number };
  size: number;
  errors: string[];
  warnings: string[];
}

export class ImageValidationService {
  private static readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  private static readonly MIN_DIMENSIONS = { width: 100, height: 100 };
  private static readonly MAX_DIMENSIONS = { width: 4096, height: 4096 };
  private static readonly ALLOWED_FORMATS = ['jpeg', 'jpg', 'png', 'webp'];
  private static readonly ALLOWED_MIME_TYPES = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/webp'
  ];

  /**
   * Valida arquivo de imagem de forma completa
   */
  static async validateImage(file: File): Promise<ImageValidationResult> {
    const result: ImageValidationResult = {
      isValid: false,
      format: '',
      dimensions: { width: 0, height: 0 },
      size: file.size,
      errors: [],
      warnings: []
    };

    try {
      // 1. Validação básica de arquivo
      if (!this.validateBasicFile(file, result)) {
        return result;
      }

      // 2. Validação de MIME type
      if (!this.validateMimeType(file, result)) {
        return result;
      }

      // 3. Validação de conteúdo real (magic bytes)
      if (!this.validateMagicBytes(file, result)) {
        return result;
      }

      // 4. Validação de dimensões
      await this.validateDimensions(file, result);

      // 5. Validação de tamanho
      this.validateFileSize(file, result);

      // 6. Validação de conteúdo suspeito
      await this.validateContent(file, result);

      // Se chegou até aqui, arquivo é válido
      result.isValid = result.errors.length === 0;

    } catch (error) {
      result.errors.push(`Erro durante validação: ${error.message}`);
    }

    return result;
  }

  /**
   * Validação básica do arquivo
   */
  private static validateBasicFile(file: File, result: ImageValidationResult): boolean {
    if (!file) {
      result.errors.push('Nenhum arquivo fornecido');
      return false;
    }

    if (file.size === 0) {
      result.errors.push('Arquivo vazio');
      return false;
    }

    if (file.size > this.MAX_FILE_SIZE) {
      result.errors.push(`Arquivo muito grande. Máximo: ${this.formatBytes(this.MAX_FILE_SIZE)}`);
      return false;
    }

    return true;
  }

  /**
   * Validação de MIME type
   */
  private static validateMimeType(file: File, result: ImageValidationResult): boolean {
    if (!this.ALLOWED_MIME_TYPES.includes(file.type)) {
      result.errors.push(`Tipo de arquivo não permitido: ${file.type}`);
      return false;
    }

    // Extrair formato do MIME type
    result.format = file.type.split('/')[1];
    return true;
  }

  /**
   * Validação de magic bytes (assinatura do arquivo)
   */
  private static async validateMagicBytes(file: File, result: ImageValidationResult): Promise<boolean> {
    try {
      const buffer = await file.slice(0, 12).arrayBuffer();
      const bytes = new Uint8Array(buffer);

      // Verificar assinaturas conhecidas
      const isValidSignature = this.checkImageSignature(bytes);
      
      if (!isValidSignature) {
        result.errors.push('Assinatura de arquivo inválida - arquivo pode não ser uma imagem real');
        return false;
      }

      return true;
    } catch (error) {
      result.errors.push(`Erro ao verificar assinatura: ${error.message}`);
      return false;
    }
  }

  /**
   * Verifica assinatura de arquivo de imagem
   */
  private static checkImageSignature(bytes: Uint8Array): boolean {
    // JPEG: FF D8 FF
    if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
      return true;
    }

    // PNG: 89 50 4E 47 0D 0A 1A 0A
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47 &&
        bytes[4] === 0x0D && bytes[5] === 0x0A && bytes[6] === 0x1A && bytes[7] === 0x0A) {
      return true;
    }

    // WebP: 52 49 46 46 ... 57 45 42 50
    if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
        bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
      return true;
    }

    return false;
  }

  /**
   * Validação de dimensões da imagem
   */
  private static async validateDimensions(file: File, result: ImageValidationResult): Promise<void> {
    try {
      const dimensions = await this.getImageDimensions(file);
      result.dimensions = dimensions;

      // Verificar dimensões mínimas
      if (dimensions.width < this.MIN_DIMENSIONS.width || dimensions.height < this.MIN_DIMENSIONS.height) {
        result.errors.push(`Imagem muito pequena. Mínimo: ${this.MIN_DIMENSIONS.width}x${this.MIN_DIMENSIONS.height}px`);
      }

      // Verificar dimensões máximas
      if (dimensions.width > this.MAX_DIMENSIONS.width || dimensions.height > this.MAX_DIMENSIONS.height) {
        result.warnings.push(`Imagem muito grande. Recomendado: máximo ${this.MAX_DIMENSIONS.width}x${this.MAX_DIMENSIONS.height}px`);
      }

      // Verificar proporção
      const aspectRatio = dimensions.width / dimensions.height;
      if (aspectRatio < 0.5 || aspectRatio > 2.0) {
        result.warnings.push('Proporção da imagem muito extrema. Recomendado: entre 0.5:1 e 2:1');
      }

    } catch (error) {
      result.errors.push(`Erro ao verificar dimensões: ${error.message}`);
    }
  }

  /**
   * Obtém dimensões da imagem
   */
  private static getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Não foi possível carregar a imagem para verificar dimensões'));
      };

      img.src = url;
    });
  }

  /**
   * Validação de tamanho do arquivo
   */
  private static validateFileSize(file: File, result: ImageValidationResult): void {
    if (file.size > this.MAX_FILE_SIZE) {
      result.errors.push(`Arquivo muito grande: ${this.formatBytes(file.size)}. Máximo: ${this.formatBytes(this.MAX_FILE_SIZE)}`);
    } else if (file.size > 2 * 1024 * 1024) { // 2MB
      result.warnings.push(`Arquivo grande: ${this.formatBytes(file.size)}. Considere comprimir para melhor performance`);
    }
  }

  /**
   * Validação de conteúdo suspeito
   */
  private static async validateContent(file: File, result: ImageValidationResult): Promise<void> {
    try {
      // Verificar se arquivo contém dados suspeitos
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);

      // Verificar se há muitos bytes nulos (pode indicar arquivo corrompido)
      const nullBytes = bytes.filter(byte => byte === 0).length;
      const nullPercentage = (nullBytes / bytes.length) * 100;

      if (nullPercentage > 50) {
        result.warnings.push('Arquivo contém muitos bytes nulos - pode estar corrompido');
      }

      // Verificar se há padrões suspeitos (ex: código executável)
      const suspiciousPatterns = this.checkSuspiciousPatterns(bytes);
      if (suspiciousPatterns.length > 0) {
        result.warnings.push(`Padrões suspeitos detectados: ${suspiciousPatterns.join(', ')}`);
      }

    } catch (error) {
      result.warnings.push(`Erro ao verificar conteúdo: ${error.message}`);
    }
  }

  /**
   * Verifica padrões suspeitos no arquivo
   */
  private static checkSuspiciousPatterns(bytes: Uint8Array): string[] {
    const patterns: string[] = [];

    // Verificar se contém cabeçalhos de executável
    if (bytes[0] === 0x4D && bytes[1] === 0x5A) { // MZ header
      patterns.push('Cabeçalho MZ (executável Windows)');
    }

    if (bytes[0] === 0x7F && bytes[1] === 0x45 && bytes[2] === 0x4C && bytes[3] === 0x46) { // ELF header
      patterns.push('Cabeçalho ELF (executável Linux)');
    }

    // Verificar se contém strings suspeitas
    const text = new TextDecoder().decode(bytes);
    if (text.includes('<?php') || text.includes('<?=')) {
      patterns.push('Código PHP detectado');
    }

    if (text.includes('<script>') || text.includes('javascript:')) {
      patterns.push('Código JavaScript detectado');
    }

    return patterns;
  }

  /**
   * Formata bytes para leitura humana
   */
  private static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Validação rápida para uso em tempo real
   */
  static quickValidate(file: File): boolean {
    return file && 
           file.size > 0 && 
           file.size <= this.MAX_FILE_SIZE &&
           this.ALLOWED_MIME_TYPES.includes(file.type);
  }
}
